// src/components/GlobalSearch.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { slugifyTeamName } from "../utils/slugify";
import { getLeagueCodeFromAny, getLeagueIdFromAny } from "../utils/leagueRouting";

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ users: [], teams: [], leagues: [] });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsOpen(false);
        setFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard shortcut: Cmd+K / Ctrl+K to focus search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults({ users: [], teams: [], leagues: [] });
      setIsOpen(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/api/search", {
          params: { q: query, limit: 5 }
        });
        if (requestId !== requestIdRef.current) return;
        setResults({
          users: data.users || [],
          teams: data.teams || [],
          leagues: data.leagues || []
        });
        setIsOpen(true);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error("Search error:", err);
        setResults({ users: [], teams: [], leagues: [] });
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, 180);

    return () => clearTimeout(timer);
  }, [query]);

  const inferLeagueIdForTeam = async (teamId, season) => {
    try {
      const params = { team_id: teamId, last: 3, next: 0 };
      if (season !== undefined && season !== null && season !== "") {
        params.season = season;
      }
      const { data } = await api.get("/football/club/fixtures", {
        params,
      });
      const played = Array.isArray(data?.played) ? data.played : [];
      const inferred = played.find((match) => Number.isFinite(Number(match?.league_id)))?.league_id;
      return Number.isFinite(Number(inferred)) ? Number(inferred) : null;
    } catch {
      return null;
    }
  };

  const handleSelect = async (type, item) => {
    setQuery("");
    setIsOpen(false);
    setFocused(false);

    switch (type) {
      case "user":
        navigate(`/users/${item.username || item.firebase_uid}`);
        break;
      case "team":
        {
          const teamId = firstDefined(
            item.csb_team_id,
            item.app_team_id,
            item.internal_team_id,
            item.team_id,
            item.id,
            item.provider_team_id
          );
          const teamName = firstDefined(item.name, item.team_name, item.team);
          const slug = item.slug || (teamName ? slugifyTeamName(teamName) : null);
          const inferredLeagueCode = getLeagueCodeFromAny({
            code: firstDefined(item.league_code, item.comp_code, item.competition_code, item.comp),
            name: firstDefined(item.league_name, item.league, item.competition_name, item.competition),
            id: firstDefined(item.league_id, item.provider_league_id, item.api_league_id),
          });
          // Search results can contain the season in which a team record was
          // indexed. That is not necessarily the current season, so club
          // navigation should let ClubPage choose its live default.
          const season = "";
          let leagueId = firstDefined(
            item.league_id,
            item.provider_league_id,
            item.api_league_id,
            getLeagueIdFromAny({ code: inferredLeagueCode })
          );
          if (!leagueId) {
            leagueId = await inferLeagueIdForTeam(teamId, season);
          }

          if (!teamId || !slug) break;

          const params = new URLSearchParams();
          if (season !== undefined && season !== null && season !== "") params.set("season", String(season));
          if (leagueId) params.set("league", String(leagueId));
          const qs = params.toString();
          navigate(`/club/${teamId}/${slug}${qs ? `?${qs}` : ""}`, {
            state: {
              club: {
                teamId,
                name: teamName,
                crest: firstDefined(item.logo, item.crest, item.team_logo),
                league: firstDefined(item.league_name, item.league, item.competition_name, item.competition),
                sport: firstDefined(item.sport, "football"),
              },
            },
          });
        }
        break;
      case "league":
        // Map league codes to appropriate sport pages
        const leagueRoutes = {
          "NFL": "/nfl",
          "NBA": "/basketball",
          "NHL": "/nhl",
          "CFB": "/cfb",
          // All football/soccer leagues go to dedicated league pages
          "EPL": "/league/EPL",
          "CHAMP": "/league/CHAMP",
          "LIGUE1": "/league/LIGUE1",
          "BUNDES": "/league/BUNDES",
          "LA_LIGA": "/league/LA_LIGA",
          "SERIE_A": "/league/SERIE_A",
          "SCO_PREM": "/league/SCO_PREM",
          "UCL": "/league/UCL",
          "UEL": "/league/UEL",
          "UECL": "/league/UECL",
        };
        const route = leagueRoutes[item.code] || `/league/${item.code}`;
        navigate(route);
        break;
      default:
        break;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setFocused(false);
      e.currentTarget.blur();
    }
  };

  const totalResults = results.users.length + results.teams.length + results.leagues.length;
  const hasResults = totalResults > 0;

  return (
    <div ref={searchRef} className="global-search-root">
      {/* Placeholder styling */}
      <style>{`
        .global-search-input::placeholder {
          color: rgba(255, 255, 255, 0.5);
          font-size: 14px;
          font-weight: 400;
        }
        .global-search-root {
          position: relative;
          width: 58px;
          height: 44px;
          z-index: 1002;
        }
        .global-search-control {
          position: absolute;
          top: 0;
          left: 0;
          width: ${focused || query ? "420px" : "58px"};
          max-width: min(420px, calc(100vw - 32px));
          transition: width 180ms ease;
          z-index: 2;
        }
        .global-search-results {
          width: min(420px, calc(100vw - 32px));
        }
        @media (max-width: 768px) {
          .global-search-root {
            width: 100%;
          }
          .global-search-control {
            position: relative;
            width: 100%;
            max-width: none;
          }
          .global-search-results {
            width: 100%;
          }
        }
      `}</style>

      {/* Search Input */}
      <div className="global-search-control" style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        pointerEvents: "auto"
      }}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search..."
          className="global-search-input"
          style={{
            width: "100%",
            padding: "10px 14px 10px 38px",
            borderRadius: 999,
            border: focused ? "1px solid #34d399" : "1px solid rgba(255,255,255,0.2)",
            background: "rgba(0,0,0,0.5)",
            color: "#fff",
            fontSize: 15,
            fontWeight: 500,
            outline: "none",
            transition: "all 0.2s",
            boxShadow: focused ? "0 0 0 3px rgba(52,211,153,0.1)" : "none",
            cursor: "text",
            pointerEvents: "auto"
          }}
        />
        <div style={{
          position: "absolute",
          left: 14,
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: 16,
          opacity: 0.6,
          pointerEvents: "none"
        }}>
          🔍
        </div>
        {loading && (
          <div style={{
            position: "absolute",
            right: 16,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 14,
            opacity: 0.5,
            pointerEvents: "none"
          }}>
            ⏳
          </div>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && query.trim() && (
        <div className="global-search-results" style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          left: 0,
          right: "auto",
          background: "#0a0f0c",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 16,
          maxHeight: 400,
          overflowY: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          zIndex: 1000
        }}>
          {!hasResults && !loading && (
            <div style={{
              padding: 24,
              textAlign: "center",
              opacity: 0.6,
              fontSize: 14
            }}>
              No results found for "{query}"
            </div>
          )}

          {/* Users */}
          {results.users.length > 0 && (
            <div>
              <div style={{
                padding: "12px 16px 8px",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                opacity: 0.5
              }}>
                Users
              </div>
              {results.users.map((user) => (
                <button
                  key={user.firebase_uid || user.username}
                  onClick={() => handleSelect("user", user)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 16px",
                    border: "none",
                    background: "transparent",
                    color: "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.2s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(52,211,153,0.1)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.display_name || user.username}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid rgba(52,211,153,0.2)"
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "rgba(52,211,153,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#34d399"
                    }}>
                      {(user.display_name || user.username || "U")[0].toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {user.display_name || user.username}
                    </div>
                    {user.username && (
                      <div style={{ fontSize: 12, opacity: 0.6 }}>
                        @{user.username}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 18, opacity: 0.3 }}>👤</div>
                </button>
              ))}
            </div>
          )}

          {/* Teams */}
          {results.teams.length > 0 && (
            <div>
              <div style={{
                padding: "12px 16px 8px",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                opacity: 0.5,
                borderTop: results.users.length > 0 ? "1px solid rgba(255,255,255,0.05)" : "none"
              }}>
                Teams
              </div>
              {results.teams.map((team, index) => (
                <button
                  key={team.team_id || team.id || team.provider_team_id || `${team.name}-${index}`}
                  onClick={() => handleSelect("team", team)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 16px",
                    border: "none",
                    background: "transparent",
                    color: "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.2s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(52,211,153,0.1)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  {team.logo ? (
                    <img
                      src={team.logo}
                      alt={team.name}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        objectFit: "contain"
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "rgba(255,255,255,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18
                    }}>
                      ⚽
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {team.name}
                    </div>
                    {team.league && (
                      <div style={{ fontSize: 12, opacity: 0.6 }}>
                        {team.league}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Leagues */}
          {results.leagues.length > 0 && (
            <div>
              <div style={{
                padding: "12px 16px 8px",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                opacity: 0.5,
                borderTop: (results.users.length > 0 || results.teams.length > 0) ? "1px solid rgba(255,255,255,0.05)" : "none"
              }}>
                Leagues
              </div>
              {results.leagues.map((league) => (
                <button
                  key={league.code}
                  onClick={() => handleSelect("league", league)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 16px",
                    border: "none",
                    background: "transparent",
                    color: "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.2s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(52,211,153,0.1)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: "rgba(52,211,153,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18
                  }}>
                    🏆
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {league.name}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.6 }}>
                      {league.code}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
